import * as React from 'react';
import {Spinner, view} from '@sail/ui';
import {useTheming} from '../../context';

type OwnProps = {
  onMount?: () => void;
  onTimeout?: () => void;
  onLoad?: () => void;
  onSuccess: () => void;
  onClose?: () => void;
  onError: () => void;
  verificationLink: string;
  locale?: string;
  frameBorder?: string;
  className?: string;
  style?: any;
  id?: string;
  loadTimeoutDelay?: number;
  height?: number;
  overrideBranding?: boolean;
  css?: any;
  loadingComponent?: JSX.Element;
};

type DefaultProps = {
  locale: OwnProps['locale'];
  loadTimeoutDelay: 45000;
};

type State = {
  loading: boolean;
  initialLoadComplete: boolean;
  iframeHeight: string | null | undefined;
  iframePadding: string | null | undefined;
  windowObject: Window | null | undefined;
};

const urlOrigin = (url: URL): string => {
  const {protocol, hostname, port} = url;
  return `${protocol}//${hostname}${port ? `:${port}` : ''}`;
};

export default class GelatoVerificationIframe extends React.Component<
  OwnProps,
  State
> {
  iframeOrigin: string;

  lastEvent: 'reload' | 'load' | 'success' | 'error' | 'resize' | null;

  lastHeight: string | null | undefined;

  // TODO(IDPROD-7354): This padding will be removed after Butter has been fully rolled out
  lastPadding: string | null | undefined;

  iframeRef: {
    current: null | HTMLIFrameElement;
  };

  loadTimeout: number | null | undefined;

  static defaultProps: DefaultProps = {
    locale: 'en',

    // If the gelato UI is not available within this many milliseconds, consider it an error. This establishes an upper bound
    // for slower clients. If a client _cannot_ load gelato within this time, they won't ever be able to make progress
    // and will see an error screen. On the other hand, with a longer timeout, they may see a spinner for a while in
    // case of a gelato outage, but slow clients have better affordance for making progress in normal conditions.
    loadTimeoutDelay: 45000,
    // TODO (bz) - maybe lower this back down to 20000 after investigating frontend analytics
  };

  constructor(props: OwnProps) {
    super(props);
    const {verificationLink} = this.props;
    this.iframeOrigin = urlOrigin(new URL(verificationLink));
    this.lastEvent = null;
    this.lastHeight = null;
    this.lastPadding = null;
    this.iframeRef = React.createRef();
    this.loadTimeout = null;
    this.state = {
      loading: true,
      initialLoadComplete: false,
      iframeHeight: null,
      iframePadding: null,
      windowObject: null,
    };
  }

  componentDidMount() {
    const {onError, onMount} = this.props;
    // When rendering across iframes, we should make sure to access the right window object where the iframe is hosted
    const windowObject = this.iframeRef.current?.ownerDocument?.defaultView;
    if (!windowObject) {
      onError();
      return;
    }

    this.setState(
      (prevState) => ({
        ...prevState,
        windowObject,
      }),
      () => {
        this.startLoadTimeout();
        this.startReceivingMessages();
        onMount && onMount();
      },
    );
  }

  // never, ever re-render the iframe
  shouldComponentUpdate(): boolean {
    return false;
  }

  componentWillUnmount() {
    this.stopReceivingMessages();
  }

  receiveMessage: (message: MessageEvent) => void = (message) => {
    const {lastEvent, iframeOrigin} = this;
    const {onLoad, onSuccess, onClose} = this.props;
    let data;

    try {
      data =
        typeof message.data === 'string'
          ? JSON.parse(String(message.data))
          : (message.data as any);
    } catch (e: any) {
      return;
    }

    if (typeof data !== 'object') {
      return;
    }

    if (message.origin !== iframeOrigin || !data.type) {
      return;
    }

    const dataType = data.type;

    const iframe = this.iframeRef.current;
    if (!iframe) {
      throw new Error(
        'GelatoVerificationIframe received a postMessage and has a null iframeRef!',
      );
    }

    // The `/start` route exchanges a unique token for an EAK and then
    // routes to `/welcome` (probably). Consider the iframe loaded
    // when the first non-start route loads.
    if (lastEvent !== dataType) {
      this.lastEvent = dataType;

      if (dataType === 'load' || dataType === 'STRIPE_IDENTITY_APP_LOAD') {
        const {initialLoadComplete} = this.state;

        // A 'reload` might cycle through loading state again, but only
        // call `onLoad` for the initial `load` event.
        onLoad && !initialLoadComplete && onLoad();

        // Actually not super sure why we need to do a forceUpdate() here
        this.setState(
          {
            loading: false,
            initialLoadComplete: true,
            iframeHeight: this.lastHeight,
            iframePadding: this.lastPadding,
          },
          () => this.forceUpdate(),
        );
      } else if (dataType === 'success') {
        onSuccess && onSuccess();
        // We assume loading is done if session was successful/closed
        this.setState({loading: false}, () => this.forceUpdate());
      } else if (dataType === 'STRIPE_IDENTITY_CLOSE') {
        onClose && onClose();
        // We assume loading is done if session was successful/closed
        this.setState({loading: false}, () => this.forceUpdate());
      } else if (dataType === 'error') {
        this.handleError();
      } else if (dataType === 'reload') {
        // The iframe is reloading itself --> go back to loading UX
        // and expect a `load` event to arrive soon. In case something
        // goes wrong and the iframe never sends a `load` after this,
        // start the load timeout.
        this.setState({loading: true}, () => this.forceUpdate());
        this.startLoadTimeout();
      }
    }

    if (dataType === 'resize') {
      // Setting the height while loading seems to make flashes of too-tall content area. So
      // capture the height here and then when `load` is posted later, whatever the
      // final height was will be set at the same time the loading state is unset.
      this.lastHeight = data.height;
      this.lastPadding = data.padding;
      const {loading} = this.state;
      if (!loading) {
        this.setState(
          {iframeHeight: data.height, iframePadding: data.padding},
          () => this.forceUpdate(),
        );
      }
    }
  };

  handleError() {
    const {onError} = this.props;
    this.stopLoadTimeout();
    this.stopReceivingMessages();
    onError && onError();
  }

  startLoadTimeout() {
    const {loadTimeoutDelay, onTimeout} = this.props;

    // eslint-disable-next-line @stripe-internal/embedded/no-restricted-globals
    this.loadTimeout = window.setTimeout(() => {
      this.stopLoadTimeout();
      const {loading} = this.state;
      if (loading) {
        // A loading UX is still showing and a timeout has happened -- ignore
        // any subsequent messages and call the parent component's onError
        // handler. Continuing to receive messages could cause an onError
        // call to be followed by an onLoad, which is confusing because onError
        // should be a terminal state.
        this.stopReceivingMessages();

        if (onTimeout) {
          onTimeout();
        } else {
          this.handleError();
        }
      }
    }, loadTimeoutDelay);
  }

  startReceivingMessages() {
    const {windowObject} = this.state;
    windowObject?.addEventListener('message', this.receiveMessage);
  }

  stopReceivingMessages() {
    const {windowObject} = this.state;
    windowObject?.removeEventListener('message', this.receiveMessage);
  }

  stopLoadTimeout() {
    if (this.loadTimeout) {
      try {
        clearTimeout(this.loadTimeout);
      } catch (e: any) {
        // do nothing
      }
      this.loadTimeout = null;
    }
  }

  render(): JSX.Element {
    const {
      verificationLink,
      locale,
      frameBorder,
      className,
      style,
      id,
      height,
      overrideBranding,
      css: cssProp,
      loadingComponent,
    } = this.props;
    const {loading, iframeHeight, iframePadding} = this.state;
    // Allow a fixed height prop to override the dynamically-reported iframe height.
    const iframeStyle = {
      ...style,
      height: loading ? '0' : height || iframeHeight,
    } as const;
    let iframeContainerPadding = 'space.250';
    if (iframePadding !== null && iframePadding !== undefined) {
      // Only override iframePadding if it's defined
      iframeContainerPadding = iframePadding;
    }

    return (
      <view.div
        css={{
          position: 'relative',
          padding: loading ? 'space.0' : iframeContainerPadding,
          width: 'fill',
          stack: 'y',
          ...(cssProp || {}),
        }}
      >
        {loading && (loadingComponent || <Spinner size="large" />)}
        <GelatoIframeWrapper
          title="GelatoIframe"
          ref={this.iframeRef}
          id={id}
          className={className}
          data-testid="gelato-verification-iframe"
          css={{position: 'relative', width: 'fill'}}
          frameBorder={frameBorder}
          style={iframeStyle}
          onLoad={() =>
            this.setState(
              (prev) => ({
                ...prev,
                loading: false,
              }),
              () => this.forceUpdate(),
            )
          }
          onError={() => this.handleError()}
          allow="camera *"
          overrideBranding={overrideBranding}
          locale={locale}
          verificationLink={verificationLink}
        />
      </view.div>
    );
  }
}

type GelatoIframeWrapperProps = {
  verificationLink: string;
  locale: string | undefined;
  overrideBranding: boolean | undefined;
};

export const GelatoIframeWrapper: React.FC<
  React.ComponentProps<typeof view.iframe> & GelatoIframeWrapperProps
> = React.forwardRef(
  ({verificationLink, locale, overrideBranding, ...props}, ref) => {
    const theming = useTheming();

    // We obtain the current value of the colorSecondary and store it in state. This state is never updated to prevent re-renders
    // The ConnectJS appearance colorPrimary determines the background color for buttons, radiobuttons, and checkboxes,
    // it actually maps to the secondary color in Gelato
    const [overrideSecondaryColor] = React.useState(
      theming?.variables?.colorPrimary,
    );

    const verificationUrl = new URL(verificationLink);

    if (locale && !verificationUrl.searchParams.has('locale')) {
      verificationUrl.searchParams.set('locale', locale);
    }

    // If overrideBranding is passed in, we do our best effort to match the platform colors
    if (overrideBranding) {
      // If we have theming values available, we use them as the color override by passing in the params to Gelato
      if (overrideSecondaryColor) {
        verificationUrl.searchParams.delete('overrideBranding');

        verificationUrl.searchParams.set(
          'overrideSecondaryColor',
          overrideSecondaryColor,
        );
      } else {
        // Otherwise we pass in the overrideBranding param to Gelato to signal that they should use the values stored by the platform
        // in their branding settings
        verificationUrl.searchParams.set('overrideBranding', 'true');
      }
    }

    return <view.iframe {...props} ref={ref} src={verificationUrl.href} />;
  },
);
